import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X, Sparkles } from "lucide-react";
import { refreshGenres } from "@/hooks/useGenres";

// Admin: add / remove AI Film sub-genres. Removing keeps the tag on existing films.
export default function AiSubgenresEditor() {
  const [subs, setSubs] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/genres").then(({ data }) => setSubs(data.ai_subgenres)).catch(() => setSubs([]));
  }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/genres/ai-subgenres", { subgenres: next });
      setSubs(data.ai_subgenres);
      refreshGenres({ ai_subgenres: data.ai_subgenres });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save sub-genres");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    if (await save([...subs, name])) { setDraft(""); toast.success(`Added “${name}”`); }
  };

  const remove = async (s) => {
    if (await save(subs.filter((x) => x !== s))) toast.success(`Removed “${s}” — existing films keep the tag`);
  };

  if (!subs) return null;
  return (
    <div className="mb-6 rounded-xl border border-white/10 bg-surface p-5" data-testid="ai-subgenres-editor">
      <p className="text-xs uppercase tracking-[0.2em] text-sky-300 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> AI Film sub-genres</p>
      <p className="text-sm text-foreground/60 mt-1">Filmmakers choose one of these when they pick the AI Film genre. Removing one hides it from new uploads only.</p>
      <div className="flex flex-wrap gap-2 mt-4">
        {subs.length === 0 && <p className="text-xs text-foreground/50">No sub-genres — filmmakers can't pick AI Film until you add one.</p>}
        {subs.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-sky-500/10 border border-sky-400/30 text-sky-100 text-xs font-semibold" data-testid={`ai-subgenre-chip-${s}`}>
            {s}
            <button type="button" onClick={() => remove(s)} disabled={busy} className="w-5 h-5 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label={`Remove ${s}`} data-testid={`ai-subgenre-remove-${s}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-2 mt-4 max-w-sm">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40} placeholder="e.g. AI-Sci-Fi" className="h-9 bg-white/5 border-white/10 text-foreground text-base sm:text-sm" data-testid="ai-subgenre-input" />
        <Button type="submit" size="sm" disabled={busy || !draft.trim()} className="h-9 bg-sky-500 hover:bg-sky-400 text-white" data-testid="ai-subgenre-add">
          <Plus className="w-4 h-4" /> Add
        </Button>
      </form>
    </div>
  );
}
